import { CatalogClient } from "@backstage/catalog-client";
import { Progress, Table, TableColumn } from "@backstage/core-components";
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from "@backstage/core-plugin-api";
import { Spacer } from "@internal/plugin-utilities";
import { Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { opsReviewApiRef } from "../../api/OpsReviewApi";
import { InformationTooltip } from "../InformationTooltip";
import { SectionHeader } from "../TeamBreakdown/SectionHeader/SectionHeader";

interface Props {
  data: any;
  loading: boolean;
}

export const IssuesAndRecommendationsSection: React.FC<Props> = ({
  data,
  loading,
}): JSX.Element => {
  const api = useApi(opsReviewApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const [issuesAndRecommendations, setIssuesAndRecommendations] = useState<
    { value: string; displayName: string; link: string }[]
  >([]);

  useEffect(() => {
    (async () => {
      const { token } = await identityApi.getCredentials();
      const catalog = new CatalogClient({ discoveryApi });

      if (data && data.length > 0) {
        const updatedData = await Promise.all(
          data.map(async (d: any) => {
            const entity = await catalog.getEntityByRef(d.user, { token });
            return {
              value: d.value,
              // @ts-ignore
              displayName: entity?.spec?.profile?.displayName,
              link: `/catalog/${entity?.metadata?.namespace?.toLowerCase()}/${entity?.kind.toLowerCase()}/${entity?.metadata?.name.toLowerCase()}`,
            };
          })
        );
        setIssuesAndRecommendations(updatedData);
      }
    })();
  }, [api, discoveryApi, identityApi, data, setIssuesAndRecommendations]);

  const tooltip = (
    <>
      <Typography variant="h5">Issues and Recommendations</Typography>
      <div>
        This section displays any issues and recommendations, as well as the
        user that added it to the board. This can be editted from the (
        <code>/ops-review/edit</code>) page.
      </div>
    </>
  );

  const header = (
    <div style={{ textAlign: "center" }}>
      <SectionHeader title="📝 Issues and Recommendations 💬" />
      <InformationTooltip title={tooltip} />
      <Spacer variant="normal" />
    </div>
  );

  if (loading) {
    return (
      <div>
        {header}
        <Progress />
      </div>
    );
  }

  const columns: TableColumn<{
    value: string;
    displayName: string;
    link: string;
  }>[] = [
    {
      title: "Issue or Recommendation",
      field: "iar",
      highlight: true,
      width: "80%",
      render: (rowData) => rowData.value,
    },
    {
      title: "Reporter",
      field: "reporter",
      highlight: true,
      width: "20%",
      render: (rowData) => <a href={rowData.link}>{rowData.displayName}</a>,
    },
  ];

  return (
    <div>
      {header}
      <Table
        options={{
          sorting: false,
          search: false,
          paging: false,
        }}
        columns={columns}
        data={issuesAndRecommendations}
        style={{
          width: "100%",
          fontSize: "1.5em",
        }}
      />
    </div>
  );
};
